/**
 * Shared Breakout Score Engine V2 breakdown UI (Investing Idea architecture
 * finalization, 2026-09-14).
 *
 * Extracted verbatim from `MarketOpportunitiesPage.tsx`'s Breakouts tab
 * (introduced there 2026-09-13) so the exact same "7 weighted indicators in
 * one table, plus a Guardrails summary" presentation can be reused by the
 * new shared `StockResearchDialog` (opened from Top Opportunities, Market
 * Cap Segments, NIFTY 50, and Portfolio Tracker) without a second
 * implementation. `MarketOpportunitiesPage.tsx`'s own Breakouts tab now
 * imports `BreakoutBreakdown` from here too — this is a pure relocation,
 * not a behavior change; every label, threshold, and formatting rule below
 * is unchanged from the original.
 *
 * Reads only already-computed, already-persisted values off
 * `BreakoutDetailResponse` (itself a read of `MarketBreakoutResult` via
 * `GET /market-opportunities/breakouts/{symbol}`) — nothing here
 * calculates or reinterprets a score. `BREAKOUT_STATUS_COLORS`/
 * `BREAKOUT_STATUS_LABELS`/`BreakoutStatusBadge`/`formatDateTime` are
 * intentionally duplicated here rather than imported from
 * `MarketOpportunitiesPage.tsx`, matching this codebase's existing
 * per-consumer small-palette convention (see `PortfolioTrackerPage.tsx`'s
 * own `STATUS_COLORS`/`STATUS_LABELS` and `StockDataPage.tsx`'s own
 * `BREAKOUT_STATUS_COLORS`/`BREAKOUT_STATUS_LABELS`, each independently
 * defined rather than sharing one module) — this file is the shared home
 * for the larger breakdown UI only, not a new cross-page palette module.
 */
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { BreakoutComponentBreakdown, BreakoutDetailResponse, BreakoutStatus } from "@/types";

export const BREAKOUT_STATUS_COLORS: Record<BreakoutStatus, string> = {
  STRONG_BREAKOUT: "#1B5E20",
  BREAKOUT_CANDIDATE: "#2E7D32",
  DEVELOPING_BREAKOUT: "#F9A825",
  NO_CONFIRMED_BREAKOUT: "#9E9E9E",
  INSUFFICIENT_DATA: "#5C6BC0",
  FAILED_BREAKOUT: "#C62828",
};

export const BREAKOUT_STATUS_LABELS: Record<BreakoutStatus, string> = {
  STRONG_BREAKOUT: "Strong Breakout",
  BREAKOUT_CANDIDATE: "Breakout Candidate",
  DEVELOPING_BREAKOUT: "Developing Breakout",
  NO_CONFIRMED_BREAKOUT: "No Confirmed Breakout",
  INSUFFICIENT_DATA: "Insufficient Data",
  FAILED_BREAKOUT: "Failed Breakout",
};

export function BreakoutStatusBadge({ status }: { status: BreakoutStatus }) {
  return (
    <Chip
      label={BREAKOUT_STATUS_LABELS[status]}
      size="small"
      sx={{ bgcolor: BREAKOUT_STATUS_COLORS[status], color: "#fff", fontWeight: 600 }}
    />
  );
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

// The expandable drawer's main table shows exactly the 7 WEIGHTED
// indicators together, in this fixed order; the two false-breakout FILTERS
// (upper wick / anchored VWAP) are adjustments/caps applied on top of that
// weighted sum, not part of the 100-point allocation, so they're surfaced
// separately in a compact "Guardrails" summary below the table instead of
// as extra indicator rows (see `BreakoutGuardrails`). Labels are the
// plain-English name of each scoring component; `formatBreakoutComponentValue`
// turns that component's raw JSON detail (see backend `scoring.py`'s
// `components` dict) into one short human-readable string per row —
// nothing here recalculates or invents a score, it only formats values the
// backend already computed.
const BREAKOUT_INDICATOR_ORDER = [
  "resistance",
  "volume",
  "ema_alignment",
  "consolidation_base",
  "relative_strength",
  "rsi",
  "adx",
] as const;

const BREAKOUT_COMPONENT_LABELS: Record<string, string> = {
  resistance: "Resistance Breakout",
  volume: "Volume Breakout",
  ema_alignment: "EMA Alignment",
  consolidation_base: "Consolidation Quality",
  relative_strength: "Relative Strength (NIFTY 50 / sector)",
  rsi: "RSI Strength",
  adx: "ADX Trend",
  upper_wick_adjustment: "Upper Wick Adjustment",
  anchored_vwap_filter: "Anchored VWAP Filter",
};

/** Compact color-coded status label per indicator — derived purely from
 * values the backend already computed (points already awarded, and the raw
 * detail fields the engine persisted), never a new scoring decision made in
 * the frontend. `color` maps to a MUI Chip `color` prop. */
function getBreakoutIndicatorStatus(
  key: string,
  component: BreakoutComponentBreakdown,
  detailResp: BreakoutDetailResponse
): { label: string; color: "success" | "warning" | "error" | "default" | "info" } {
  const d = component.detail;
  // Audit remediation: an UNAVAILABLE input is shown as such — never as a low score.
  if (component.available === false) return { label: "Data missing", color: "warning" };
  switch (key) {
    case "resistance":
      if (!detailResp.resistance_breakout) return { label: "Not Confirmed", color: "default" };
      if (d.broke_252) return { label: "Confirmed (52W)", color: "success" };
      if (d.broke_50) return { label: "Confirmed (50D)", color: "success" };
      return { label: "Confirmed", color: "success" };
    case "volume":
      return detailResp.volume_meaningful
        ? { label: "High Volume", color: "success" }
        : { label: "Low Volume", color: "default" };
    case "ema_alignment":
      if (component.points >= 15) return { label: "Strong Bullish", color: "success" };
      if (component.points > 0) return { label: "Bullish", color: "info" };
      return { label: "Not Aligned", color: "default" };
    case "consolidation_base":
      return component.points > 0 ? { label: "Squeeze", color: "info" } : { label: "None", color: "default" };
    case "relative_strength":
      if (!d.data_available) return { label: "N/A", color: "default" };
      if (component.points >= 10) return { label: "Strong", color: "success" };
      if (component.points > 0) return { label: "Improving", color: "warning" };
      return { label: "Weak", color: "default" };
    case "rsi":
      if (component.points === 10) return { label: "Ideal Zone", color: "success" };
      if (component.points === 6) return { label: "Extended", color: "warning" };
      if (component.points === 2) return { label: "Overbought", color: "error" };
      return { label: "Neutral", color: "default" };
    case "adx":
      return component.points > 0 ? { label: "Strong Trend", color: "success" } : { label: "Weak Trend", color: "default" };
    default:
      return { label: "—", color: "default" };
  }
}

function fmtNum(v: unknown, decimals = 2): string {
  return typeof v === "number" ? v.toFixed(decimals) : "—";
}

function formatBreakoutComponentValue(key: string, detail: Record<string, unknown>): string {
  switch (key) {
    case "resistance": {
      const parts: string[] = [];
      if (typeof detail.prior_high_20 === "number") {
        parts.push(`20d High ₹${fmtNum(detail.prior_high_20)}${detail.broke_20 ? " ✓" : ""}`);
      }
      if (typeof detail.prior_high_50 === "number") {
        parts.push(`50d High ₹${fmtNum(detail.prior_high_50)}${detail.broke_50 ? " ✓" : ""}`);
      }
      if (typeof detail.prior_high_252 === "number") {
        parts.push(`52w High ₹${fmtNum(detail.prior_high_252)}${detail.broke_252 ? " ✓" : ""}`);
      }
      return parts.length > 0 ? parts.join(" · ") : "Not enough history yet";
    }
    case "volume":
      return typeof detail.volume_ratio === "number" ? `${fmtNum(detail.volume_ratio)}x prior-20-day avg volume` : "—";
    case "ema_alignment": {
      const parts: string[] = [];
      if (typeof detail.ema_20 === "number") parts.push(`EMA20 ₹${fmtNum(detail.ema_20)}`);
      if (typeof detail.ema_50 === "number") parts.push(`EMA50 ₹${fmtNum(detail.ema_50)}`);
      if (typeof detail.ema_200 === "number") parts.push(`EMA200 ₹${fmtNum(detail.ema_200)}`);
      return parts.length > 0 ? parts.join(" · ") : "—";
    }
    case "consolidation_base":
      if (typeof detail.bb_width !== "number") return "—";
      return `BB Width ${fmtNum(detail.bb_width)}%${
        typeof detail.percentile_rank === "number" ? `, ${fmtNum(detail.percentile_rank, 0)}th percentile` : ""
      }`;
    case "relative_strength":
      if (!detail.data_available) return "NIFTY 50 data not available";
      return [
        typeof detail.diff_pct === "number"
          ? `${detail.diff_pct >= 0 ? "+" : ""}${fmtNum(detail.diff_pct)}% vs NIFTY 50`
          : null,
        typeof detail.sector_diff_pct === "number"
          ? `${detail.sector_diff_pct >= 0 ? "+" : ""}${fmtNum(detail.sector_diff_pct)}% vs sector`
          : "sector benchmark unavailable",
      ]
        .filter(Boolean)
        .join(" · ");
    case "rsi":
      return typeof detail.rsi_14 === "number" ? fmtNum(detail.rsi_14, 1) : "—";
    case "adx":
      return typeof detail.adx_14 === "number"
        ? `ADX ${fmtNum(detail.adx_14, 1)} (+DI ${fmtNum(detail.plus_di, 1)} / -DI ${fmtNum(detail.minus_di, 1)})`
        : "—";
    case "upper_wick_adjustment":
      return typeof detail.upper_wick_ratio === "number" ? `Wick Ratio ${fmtNum(Number(detail.upper_wick_ratio) * 100, 0)}%` : "—";
    case "anchored_vwap_filter":
      if (!detail.swing_high_found) return "No confirmed prior swing high";
      return typeof detail.anchored_vwap === "number" ? `Anchored VWAP ₹${fmtNum(detail.anchored_vwap)}` : "—";
    default:
      return "—";
  }
}

/** Guardrails summary — the two false-breakout FILTERS (adjustments/caps
 * applied on top of the 7-indicator weighted sum, not part of the 100-point
 * allocation), plus the final score/status. Values are read straight off
 * the persisted result (`vwap_cap_applied` flag + the `upper_wick_
 * adjustment` component's own points) — no new logic. */
export function BreakoutGuardrails({ detail }: { detail: BreakoutDetailResponse }) {
  const wick = detail.components.upper_wick_adjustment;
  const wickPassed = !wick || wick.points >= 0;
  const vwapPassed = !detail.vwap_cap_applied;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Stack spacing={0.75}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Upper Wick Check
          </Typography>
          <Chip
            size="small"
            label={wickPassed ? "Passed" : `Penalty ${wick?.points ?? 0}`}
            color={wickPassed ? "success" : "error"}
            variant={wickPassed ? "outlined" : "filled"}
          />
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Anchored VWAP
          </Typography>
          <Chip
            size="small"
            label={vwapPassed ? "Passed" : "Score Cap Applied"}
            color={vwapPassed ? "success" : "warning"}
            variant={vwapPassed ? "outlined" : "filled"}
          />
        </Stack>
      </Stack>
      <Divider sx={{ my: 1.5 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={700}>
          Breakout Score: {Math.round(detail.breakout_score)} / 100
        </Typography>
        <BreakoutStatusBadge status={detail.status} />
      </Stack>
    </Box>
  );
}

/** Breakout Score expandable drawer — all 7 weighted indicators together in
 * ONE table (Indicator | Condition/Metric | Score | Status), plus the
 * Guardrails summary and final score below. Shows a small spinner/error
 * inline rather than blocking the whole surrounding view (dialog or
 * in-page drawer, whichever consumer renders it). Horizontally scrolls on
 * narrow screens (via `TableContainer`) rather than breaking column
 * alignment. */
export function BreakoutBreakdown({
  detail,
  loading,
  error,
}: {
  detail: BreakoutDetailResponse | undefined;
  loading: boolean;
  error: string | undefined;
}) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ my: 1 }}>
        {error}
      </Alert>
    );
  }
  if (!detail) return null;

  return (
    <Box sx={{ py: 1.5, px: 1 }}>
      <Stack direction="row" spacing={2} flexWrap="wrap" mb={1.5} rowGap={0.5}>
        <Typography variant="caption" color="text.secondary">
          As of {detail.trade_date} · Calculated {formatDateTime(detail.computed_at)} · Engine {detail.calculation_version}
        </Typography>
        {detail.stale && <Chip size="small" color="warning" label="Stale — not the latest completed session" />}
        {(detail.review_flags ?? []).map((flag) => (
          <Chip key={flag} size="small" variant="outlined" color="info" label={`Review: ${flag.replace(/_/g, " ")}`} />
        ))}
      </Stack>
      {detail.data_complete === false && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Score not computed — required data missing: {(detail.missing_inputs ?? []).join(", ") || "unknown"}. This is
          a data gap, not a low score.
        </Alert>
      )}
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Indicator</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Condition / Metric</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                Score
              </TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {BREAKOUT_INDICATOR_ORDER.filter((key) => detail.components[key]).map((key) => {
              const component = detail.components[key];
              const status = getBreakoutIndicatorStatus(key, component, detail);
              return (
                <TableRow key={key}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{BREAKOUT_COMPONENT_LABELS[key]}</TableCell>
                  <TableCell>{formatBreakoutComponentValue(key, component.detail)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {component.points > 0 ? "+" : ""}
                    {component.points}
                    {component.max_points ? ` / ${component.max_points}` : ""}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={status.label} color={status.color} variant="outlined" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <BreakoutGuardrails detail={detail} />
    </Box>
  );
}
